import { z } from "zod";

import type { KpiSnapshot } from "@/api/kpi";

/**
 * "Seviye" sekmesi — eski arayuzle esitlik (gorev B): veri kaynagi
 * `/api/v2/stok/acil` DEGIL, `useKpiSnapshot` (`/api/v2/kpi/snapshot`) →
 * `kpis.kritik_stok.subitems`. Bu subitems `backend/app/kpi/calculator.py`
 * `kpi_kritik_stok` (satir ~250-403) tarafindan uretilir ve
 * `backend/app/core/kpi/snapshot.py` `calculate_all_for_tenant` degismeden
 * cagirip `core.snapshots(kind='kpi')`'a yazar — eski `/api/snapshot`
 * `kritik_stok.subitems` ile AYNI sekil/alanlar (legacy/js/stok.js
 * `renderStokSeviye`): tamamen_tukenen / bedeni_eksik / azalan, her biri
 * {title, count, kartlar:[{product_card_id, name, category, supplier, sku,
 * fiyat, min_stock, total_stock, variant_count, varyantlar:[{sku, beden,
 * stock}], ...}]}.
 *
 * BILINEN EKSIK (rapor icin): kartlarda `admin_link` YOK — yalniz
 * `product_card_id`. `/api/v2/kpi/{kpi_id}/items` ucu (app/core/api/kpi.py
 * `_ADMIN_LINK_SPEC`) varyant-bazli HAM satirlara admin_link ekliyor ama bu
 * `subitems.kartlar` (kart-bazli, kpi/snapshot.py'nin tasidigi payload)
 * icin GECERLI DEGIL — bu yuzden bu sekmede Ticimax deep-link su an render
 * OLMUYOR (DeepLink href=null -> duz metin). Kalici cozum
 * `admin_link_for(tenant, "product", kart.product_card_id)` cagrisini
 * `core/api/kpi.py kpi_snapshot_endpoint` (ya da `kpi_kritik_stok`) icine
 * eklemek olur; ikisi de bu gorevde izin verilen dosya degil (yalniz
 * `core/api/stok.py` — ek alan/uc icin — izinli), o yuzden burada
 * YAPILMADI.
 */

const seviyeVaryantSchema = z
  .object({
    sku: z.string().nullable().optional(),
    beden: z.string().nullable().optional(),
    stock: z.number(),
  })
  .passthrough();

const seviyeKartSchema = z
  .object({
    product_card_id: z.string(),
    name: z.string().optional(),
    category: z.string().nullable().optional(),
    supplier: z.string().nullable().optional(),
    sku: z.string().nullable().optional(),
    fiyat: z.number().nullable().optional(),
    min_stock: z.number().optional(),
    total_stock: z.number(),
    variant_count: z.number().optional(),
    varyantlar: z.array(seviyeVaryantSchema).default([]),
    tukenen_varyant_sayisi: z.number().optional(),
    dolu_varyant_sayisi: z.number().optional(),
    // Henuz backend'den gelmiyor (yukaridaki not) — semada tutulur, DeepLink
    // yoksa duz metne duser (api/stok.ts StokRow.admin_link ile ayni desen).
    admin_link: z.string().nullable().optional(),
  })
  .passthrough();
export type StokSeviyeKart = z.infer<typeof seviyeKartSchema>;

const seviyeBolumSchema = z.object({
  title: z.string().optional(),
  count: z.number(),
  kartlar: z.array(seviyeKartSchema),
});

const kritikStokSubitemsSchema = z
  .object({
    subitems: z.object({
      tamamen_tukenen: seviyeBolumSchema,
      bedeni_eksik: seviyeBolumSchema,
      azalan: seviyeBolumSchema,
    }),
  })
  .passthrough();

export type StokSeviyeBolumKey = "tamamen_tukenen" | "bedeni_eksik" | "azalan";
type SeviyeTitleKey = "stok.seviye.tamamen_tukenen" | "stok.seviye.bedeni_eksik" | "stok.seviye.azalan";

export interface StokSeviyeBolum {
  key: StokSeviyeBolumKey;
  titleKey: SeviyeTitleKey;
  count: number;
  kartlar: StokSeviyeKart[];
}

const BOLUM_SIRASI: readonly StokSeviyeBolumKey[] = ["tamamen_tukenen", "bedeni_eksik", "azalan"];
const BOLUM_TITLE_KEYS: Record<StokSeviyeBolumKey, SeviyeTitleKey> = {
  tamamen_tukenen: "stok.seviye.tamamen_tukenen",
  bedeni_eksik: "stok.seviye.bedeni_eksik",
  azalan: "stok.seviye.azalan",
};

export type StokSeviyeParseResult =
  | { status: "empty" } // kpi snapshot yok (404/henuz uretilmedi) ya da kritik_stok alani hic yok
  | { status: "error" } // kritik_stok VAR ama beklenen sekilde degil (sema sapmasi — sessizce yutulmaz)
  | { status: "ok"; bolumler: StokSeviyeBolum[] };

/**
 * `KpiSnapshot.kpis.kritik_stok` (gevsek tipli — bkz. api/kpi.ts kpiSnapshotSchema
 * JSDoc'u) → sabit sirali 3 bolum. `snapshot` `null`/`undefined` (henuz
 * yuklenmedi ya da 404 `snapshot_yok`) veya `kritik_stok` anahtari yoksa
 * "empty"; anahtar VAR ama sema uyusmuyorsa "error" (veri sessizce "yok"
 * gibi gosterilmez).
 */
export function parseStokSeviye(snapshot: KpiSnapshot | null | undefined): StokSeviyeParseResult {
  const raw = snapshot?.kpis?.kritik_stok;
  if (raw == null) return { status: "empty" };
  const parsed = kritikStokSubitemsSchema.safeParse(raw);
  if (!parsed.success) return { status: "error" };
  const s = parsed.data.subitems;
  return {
    status: "ok",
    bolumler: BOLUM_SIRASI.map((key) => ({
      key,
      titleKey: BOLUM_TITLE_KEYS[key],
      count: s[key].count,
      kartlar: s[key].kartlar,
    })),
  };
}

/** Ad/SKU/varyant SKU'suna gore basit istemci-tarafi arama (bos sorguda tum kartlar). */
export function filterSeviyeKartlar(kartlar: StokSeviyeKart[], query: string): StokSeviyeKart[] {
  const q = query.trim().toLowerCase();
  if (!q) return kartlar;
  return kartlar.filter(
    (k) =>
      (k.name || "").toLowerCase().includes(q) ||
      (k.sku || "").toLowerCase().includes(q) ||
      k.varyantlar.some((v) => (v.sku || "").toLowerCase().includes(q)),
  );
}
